import { useQuery } from '@tanstack/react-query';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, authApi, type CaptchaInput } from '../../lib/api';

/**
 * Cloudflare Turnstile on the public forms (D-160). The server decides when a
 * challenge is owed; this file renders the widget and carries its token.
 *
 * The forms that send e-mail (register, forgot password, resend verification)
 * show the widget whenever Turnstile is configured. Sign in shows it only once
 * the server answered `captchaRequired` — after repeated failures from this
 * client — so a normal sign-in never sees it.
 *
 * A token is single-use: after any refused submission the widget is reset, or
 * the next attempt would fail on a spent token with nothing to explain why.
 */

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export type TurnstileAction = 'register' | 'resend-verification' | 'password-forgot' | 'login';

interface TurnstileRenderOptions {
  sitekey: string;
  action: TurnstileAction;
  language: string;
  theme: 'auto';
  callback: (token: string) => void;
  'expired-callback': () => void;
  'error-callback': () => void;
}

/** The subset of Cloudflare's explicit-rendering API this file uses. */
export interface TurnstileApi {
  render(container: HTMLElement, options: TurnstileRenderOptions): string;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptLoading: Promise<TurnstileApi> | null = null;

/** Loads Cloudflare's script once, on the first form that needs it. */
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  scriptLoading ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Turnstile script loaded without its API'));
    };
    script.onerror = () => {
      scriptLoading = null;
      reject(new Error('Turnstile script failed to load'));
    };
    document.head.appendChild(script);
  });
  return scriptLoading;
}

/** The site key (null when the challenge is off), and whether it is still being fetched. Once per session. */
export function useTurnstileSiteKey(): { siteKey: string | null; isPending: boolean } {
  const { data, isPending } = useQuery({ queryKey: ['auth-config'], queryFn: authApi.config, staleTime: Infinity, retry: 2 });
  return { siteKey: data?.turnstileSiteKey ?? null, isPending };
}

export interface TurnstileHandle {
  /** Discards the current token and asks for a fresh challenge. */
  reset(): void;
}

export const TurnstileWidget = forwardRef<
  TurnstileHandle,
  { siteKey: string; action: TurnstileAction; onToken: (token: string | null) => void }
>(function TurnstileWidget({ siteKey, action, onToken }, ref) {
  const { i18n } = useTranslation();
  const container = useRef<HTMLDivElement>(null);
  const widget = useRef<{ api: TurnstileApi; id: string } | null>(null);
  // The latest callback, so the widget never calls a stale closure.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    let cancelled = false;
    void loadTurnstile()
      .then((api) => {
        if (cancelled || !container.current) return;
        const id = api.render(container.current, {
          sitekey: siteKey,
          action,
          language: i18n.language,
          theme: 'auto',
          callback: (token) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(null),
          'error-callback': () => onTokenRef.current(null),
        });
        widget.current = { api, id };
      })
      .catch(() => {
        // The script is blocked or down: nothing renders, the submit stays
        // disabled with the "in progress" line, and the server would refuse
        // anyway (closed on failure).
      });
    return () => {
      cancelled = true;
      if (widget.current) {
        widget.current.api.remove(widget.current.id);
        widget.current = null;
      }
    };
    // Language and action are fixed for the life of a form: only the site key
    // (one value per session, resolved once) can re-render the widget.
  }, [siteKey]);

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widget.current) widget.current.api.reset(widget.current.id);
      onTokenRef.current(null);
    },
  }));

  return <div ref={container} data-testid="turnstile" />;
});

/** True for the three `CAPTCHA_*` refusals and for a 401 that flags the next attempt. */
export function isCaptchaError(err: unknown): boolean {
  return err instanceof ApiError && err.details?.captchaRequired === true;
}

export interface Captcha {
  /** Rendered between the fields and the submit button; null when nothing is owed. */
  widget: ReactNode;
  /** What to spread into the request body. */
  input: CaptchaInput;
  /** The widget is up but not solved yet: the submit waits. */
  pending: boolean;
  /** Call on every refused submission: resets the widget (tokens are single-use) and, on sign in, starts showing it. */
  onError: (err: unknown) => void;
}

/**
 * A form's challenge state. `always` forms show the widget whenever Turnstile
 * is configured; the sign-in form passes `false` and shows it only after the
 * server asked.
 */
export function useCaptcha(action: TurnstileAction, always: boolean): Captcha {
  const { t } = useTranslation();
  const { siteKey, isPending: configPending } = useTurnstileSiteKey();
  const [requested, setRequested] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const handle = useRef<TurnstileHandle>(null);

  const shown = siteKey !== null && (always || requested);
  // An "always" form must not submit before it knows whether a challenge is
  // owed: a fast submit would go out without a token and be refused, with no
  // widget on screen to act on.
  const pending = (always && configPending) || (shown && token === null);

  return {
    widget: shown ? (
      <div className="flex flex-col gap-1.5">
        <TurnstileWidget ref={handle} siteKey={siteKey} action={action} onToken={setToken} />
        {token ? null : (
          <span className="text-ds-xs text-ink-3" aria-live="polite">
            {t('auth.captcha.pending')}
          </span>
        )}
      </div>
    ) : null,
    input: token ? { turnstileToken: token } : {},
    pending,
    onError: (err) => {
      if (isCaptchaError(err)) setRequested(true);
      handle.current?.reset();
      setToken(null);
    },
  };
}
